import "@/App.css";
import "./pages/Landing.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ParkProvider } from "./contexts/ParkContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ProveedorRegistro from "./pages/ProveedorRegistro";
import ProveedorDashboard from "./pages/ProveedorDashboard";
import Proveedores from "./pages/Proveedores";
import Lotes from "./pages/Lotes";
import Directorio from "./pages/Directorio";
import Presupuesto from "./pages/Presupuesto";
import Cuotas from "./pages/Cuotas";
import Agua from "./pages/Agua";
import Gastos from "./pages/Gastos";
import Reportes from "./pages/Reportes";
import Usuarios from "./pages/Usuarios";
import Configuraciones from "./pages/Configuraciones";
import MiCuenta from "./pages/MiCuenta";

// Role-based access levels
// propietario: Only user management (masters)
// master: Full access to parks they own + user management
// admin: Full operational access to assigned parks
// consulta: Read-only access to assigned lots
// proveedor: Can upload invoices during allowed windows

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If no specific roles required, allow any authenticated user
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.rol)) {
    // Redirect based on role
    if (user.rol === 'propietario') {
      return <Navigate to="/usuarios" replace />;
    } else if (user.rol === 'consulta') {
      return <Navigate to="/mi-cuenta" replace />;
    } else if (user.rol === 'proveedor') {
      return <Navigate to="/portal-proveedor" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <Layout>{children}</Layout>;
};

const getDefaultRoute = (user) => {
  if (!user) return '/login';
  switch (user.rol) {
    case 'propietario':
      return '/usuarios';
    case 'master':
    case 'admin':
      return '/dashboard';
    case 'consulta':
      return '/mi-cuenta';
    case 'proveedor':
      return '/portal-proveedor';
    default:
      return '/login';
  }
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<Landing />} />
      
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to={getDefaultRoute(user)} replace /> : <Login />} 
      />
      <Route path="/registro-proveedor" element={<ProveedorRegistro />} />
      
      {/* Proveedor Portal - Only for approved suppliers */}
      <Route path="/portal-proveedor" element={
        user?.rol === 'proveedor' ? <ProveedorDashboard /> : <Navigate to="/login" replace />
      } />
      
      {/* Operational Routes - Master and Admin only */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/lotes" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Lotes />
        </ProtectedRoute>
      } />
      <Route path="/directorio" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Directorio />
        </ProtectedRoute>
      } />
      <Route path="/presupuesto" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Presupuesto />
        </ProtectedRoute>
      } />
      <Route path="/cuotas" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Cuotas />
        </ProtectedRoute>
      } />
      <Route path="/agua" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Agua />
        </ProtectedRoute>
      } />
      <Route path="/gastos" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Gastos />
        </ProtectedRoute>
      } />
      <Route path="/proveedores" element={
        <ProtectedRoute allowedRoles={['propietario', 'master', 'admin']}>
          <Proveedores />
        </ProtectedRoute>
      } />
      <Route path="/configuraciones" element={
        <ProtectedRoute allowedRoles={['master', 'admin']}>
          <Configuraciones />
        </ProtectedRoute>
      } />
      
      {/* Reports - Master, Admin, and Consulta */}
      <Route path="/reportes" element={
        <ProtectedRoute allowedRoles={['master', 'admin', 'consulta']}>
          <Reportes />
        </ProtectedRoute>
      } />
      
      {/* User Management - Propietario and Master */}
      <Route path="/usuarios" element={
        <ProtectedRoute allowedRoles={['propietario', 'master']}>
          <Usuarios />
        </ProtectedRoute>
      } />
      
      {/* Consulta User Route */}
      <Route path="/mi-cuenta" element={
        <ProtectedRoute allowedRoles={['consulta']}>
          <MiCuenta />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ParkProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </ParkProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
